/**
 * Walk a limit/offset list endpoint (httpx.List: {items,total,limit,offset}; server
 * default 50, max 200) until every row is fetched. Bounded by maxPages so a list that
 * keeps growing cannot loop forever; `truncated` says when it stopped early, so a screen
 * can disclose it instead of silently showing a partial list.
 *
 * The END of the list is a SHORT page, not the server's `total`. Several list handlers
 * (ListProducts among them) answer `Total: len(items)` — the page's length — so trusting
 * `total` made a full first page look like everything (a plant's few hundred products stopped at 200).
 * The next offset is the number of rows already received, so a server that clamps the
 * page below the size asked for is followed rather than skipped over.
 */
export interface Page<T> {
  items: T[];
  total: number;
  limit?: number;
  offset?: number;
}

export interface AllPages<T> {
  items: T[];
  total: number;
  truncated: boolean;
}

export async function collectAllPages<T>(
  fetchPage: (offset: number, limit: number) => Promise<Page<T>>,
  pageSize = 200,
  maxPages = 50,
): Promise<AllPages<T>> {
  const items: T[] = [];
  let reported = 0;
  for (let page = 0; page < maxPages; page++) {
    const res = await fetchPage(items.length, pageSize);
    items.push(...res.items);
    reported = Math.max(reported, res.total ?? 0);
    const served = res.limit && res.limit > 0 ? Math.min(res.limit, pageSize) : pageSize;
    if (res.items.length === 0 || res.items.length < served) {
      return { items, total: Math.max(items.length, reported), truncated: false };
    }
  }
  return { items, total: Math.max(items.length, reported), truncated: true };
}
